const importCache = new Map<string, Promise<void>>();

const normalizePathname = (rawPath: string): string => {
  const trimmed = rawPath.trim();
  if (trimmed.length === 0) return '/';
  const withoutQuery = trimmed.split('?')[0] ?? trimmed;
  const withoutHash = (withoutQuery.split('#')[0] ?? withoutQuery).trim();
  if (withoutHash.length === 0) return '/';
  return withoutHash.startsWith('/') ? withoutHash : `/${withoutHash}`;
};

const runCachedImport = async (key: string, run: () => Promise<unknown>): Promise<void> => {
  const existing = importCache.get(key);
  if (existing) return existing;
  const promise = run().then(() => undefined);
  importCache.set(key, promise);
  return promise;
};

export const preloadRouteForPath = (rawPath: string): Promise<void> | null => {
  const pathname = normalizePathname(rawPath);

  if (pathname === '/execution-runs') {
    return runCachedImport(
      'execution-runs',
      async () => import('@/features/executionRuns/pages/ExecutionRunsPage'),
    );
  }
  if (
    pathname === '/coverage-runs' ||
    pathname === '/coverage-directories' ||
    pathname === '/coverage-files'
  ) {
    return runCachedImport('coverage', async () => import('@/features/codeCoverage/routes'));
  }
  if (
    pathname.startsWith('/coverage-runs/') ||
    pathname.startsWith('/coverage-directories/') ||
    pathname.startsWith('/coverage-files/')
  ) {
    return runCachedImport('coverage-detail', async () => import('@/features/codeCoverage/routes'));
  }
  if (pathname.startsWith('/execution-runs/')) {
    return runCachedImport(
      'execution-run-detail',
      async () => import('@/features/executionRuns/pages/ExecutionRunDetailPage'),
    );
  }
  if (pathname === '/project-settings') {
    return runCachedImport('project-settings', async () => import('@/features/projects/routes'));
  }
  if (pathname === '/organisation-settings') {
    return runCachedImport(
      'organisation-settings',
      async () => import('@/features/organisations/pages/OrganisationSettingsPage'),
    );
  }

  if (pathname === '/releases' || pathname.startsWith('/releases/')) {
    return runCachedImport('releases', async () => import('@/features/releases/routes'));
  }
  if (pathname === '/features' || pathname.startsWith('/features/')) {
    return runCachedImport('features', async () => import('@/features/features/routes'));
  }

  if (pathname === '/suites' || pathname.startsWith('/suites/')) {
    return runCachedImport('suites', async () => import('@/features/suites/routes'));
  }
  if (pathname === '/tests' || pathname.startsWith('/tests/')) {
    return runCachedImport('tests', async () => import('@/features/tests/routes'));
  }

  if (pathname === '/pipelines' || pathname.startsWith('/pipelines/')) {
    return runCachedImport(
      'pipelines',
      async () => import('@/features/pipelines/pages/PipelinesPage'),
    );
  }
  if (pathname === '/stages' || pathname.startsWith('/stages/')) {
    return runCachedImport('stages', async () => import('@/features/pipelines/pages/StagesPage'));
  }
  if (pathname === '/jobs' || pathname.startsWith('/jobs/')) {
    return runCachedImport('jobs', async () => import('@/features/jobs/routes'));
  }

  if (pathname === '/account-settings') {
    return runCachedImport('account-settings', async () => import('@/pages/UserSettingsPage'));
  }
  if (pathname === '/plan-settings') {
    return runCachedImport('plan-settings', async () => import('@/pages/PlanPage'));
  }
  if (pathname === '/superuser') {
    return runCachedImport('superuser', async () => import('@/pages/SuperuserPage'));
  }

  return null;
};
